import { MinusCircleFilled } from "@ant-design/icons";
import { RepetitionIcon } from "@assets/icons/RepetitionIcon";
import {
  ButtonAddCard,
  KButton,
  KFormUser,
  KLgText700,
  KSmTextSecondary50,
  KText,
} from "@components";
import { MAX_REPETITION_DATES } from "@constants/card";
import { QuestionItem } from "@pages/card-list/components/item";
import { getCardDetailsService } from "@services";
import {
  Color,
  ECardType,
  ECardTypeName,
  ICardAppointmentRes,
  ICardPractitionerRes,
  IKSelectOption,
  IParams,
} from "@types";
import { Divider, Flex } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { AttachmentsDetail } from "./Attachments";
import { SetReminderSection } from "./SetReminderSection";

export const AppointmentDetail = () => {
  const {
    getValues,
    watch,
    setValue,
    formState: { dirtyFields },
    control,
  } = useFormContext<ICardAppointmentRes>();
  const { append, remove } = useFieldArray<any>({
    control,
    name: "repetition.sequenceDates",
  });
  const { profileId } = useParams<IParams>();
  const [isShowDropdown, setIsShowDropdown] = useState<boolean>(false);
  const [options, setOptions] = useState<IKSelectOption[]>([]);
  const [isOpenSelect, setIsOpenSelect] = useState<boolean>(false);

  useEffect(() => {
    const getLocation = async () => {
      const practitionerId = watch("practitioner");
      if (!profileId || !practitionerId) return;
      if (dirtyFields["practitioner"]) {
        setIsOpenSelect(true);
      }
      const practitioner = await getCardDetailsService<ICardPractitionerRes>(
        profileId,
        ECardTypeName.Practitioners,
        practitionerId
      );
      const locationsMapped = practitioner.multipleLocations?.reduce(
        (acc: { value: string; label: string }[], value: string) => {
          acc.push({ value, label: value });
          return acc;
        },
        []
      );
      setIsShowDropdown(true);
      if (locationsMapped) {
        setOptions(locationsMapped);
      }
      if (practitioner) {
        if (practitioner.location) {
          setValue("location", practitioner.location);
        } else if (!!practitioner.multipleLocations?.length) {
          setValue("multipleLocations", []);
        }
      }
    };

    getLocation();
  }, [watch("practitioner")]);

  const shouldShowDropdown = useMemo(() => {
    return (
      isShowDropdown && watch("practitioner") && getValues("multipleLocations")
    );
  }, [isShowDropdown, watch("practitioner"), watch("multipleLocations")]);

  const isLimitedOfRepetition = useMemo(
    () => watch("repetition.sequenceDates")?.length >= MAX_REPETITION_DATES,
    [watch("repetition.sequenceDates")]
  );

  const questions = useMemo(() => watch("questions"), [watch("questions")]);
  return (
    <>
      <KFormUser.DateTimePicker
        timeLabel="Time"
        label="Date"
        name="startTime"
      />
      <KFormUser.OptionsSelector
        label="Primary Practitioner"
        cardType={ECardType.Practitioners}
      />
      {shouldShowDropdown && (
        <KFormUser.Select
          open={isOpenSelect}
          label="Practitioner location"
          name="multipleLocations"
          placeholder="Select practitioner location"
          options={options}
          onChange={(selectedValue) => {
            if (selectedValue) {
              setValue("location", selectedValue);
              setIsOpenSelect(false);
            }
          }}
          onDropdownVisibleChange={(open) => {
            if (open !== isOpenSelect) {
              setIsOpenSelect(open);
            }
          }}
        />
      )}
      <KFormUser.TextArea label="Location" name="location" />
      <KFormUser.TextArea label="Additional Information" name="description" />
      <KFormUser.Switch
        icon={<RepetitionIcon />}
        title="Repetition"
        name="repetition.isSetRepetition"
      />
      {watch("repetition.isSetRepetition") && (
        <>
          <Flex gap={16} vertical>
            {watch("repetition.sequenceDates")?.map((_, idx) => (
              <Flex vertical gap={8} key={idx}>
                <Flex justify="space-between" align="center">
                  <KText>Repetition {idx + 1}</KText>
                  <MinusCircleFilled
                    onClick={() => remove(idx)}
                    style={{ fontSize: 16, color: Color.ErrorBase }}
                  />
                </Flex>
                <KFormUser.DateTimePicker
                  key={idx}
                  placeholder="-None-"
                  name={`repetition.sequenceDates[${idx}]`}
                />
              </Flex>
            ))}
          </Flex>
          <Flex flex={1} justify="center" align="center" vertical>
            {!isLimitedOfRepetition ? (
              <KButton
                ghost
                size="small"
                title="Add Another Repetition"
                onClick={() => append(null)}
                style={{ width: "fit-content" }}
              />
            ) : (
              <KSmTextSecondary50>
                To create more repetitions, copy this appointment
              </KSmTextSecondary50>
            )}
          </Flex>
        </>
      )}
      <AttachmentsDetail />
      <SetReminderSection />
      <Divider />
      <Flex vertical gap={16}>
        <Flex justify="space-between" align="center">
          <KLgText700>Active Questions</KLgText700>
          <ButtonAddCard cardType={ECardType.Questions} />
        </Flex>
        {questions?.map((qs) => (
          <QuestionItem card={qs} key={qs._id} isDetail />
        ))}
      </Flex>
    </>
  );
};
